import { z } from 'zod';

export type ColorMode = 'light' | 'dark';

// Default colors: the Sunfish brand orange over a cool, slate-neutral palette.
// primaryForeground is a warm near-black because white on the orange fails
// contrast. Optional roles (muted/accent/border/input/ring) keep the branded
// surface's utilities styled and are tuned to the cool neutrals above.
const DEFAULT_COLORS = {
	primary: '#ffbf00',
	primaryForeground: '#1f1604',
	secondary: '#e3e1de',
	secondaryForeground: '#374151',
	background: '#f8fafc',
	foreground: '#212529',
	destructive: '#dc2626',
	destructiveForeground: '#ffffff',
	info: '#0ea5e9',
	infoForeground: '#ffffff',
	layout: '#1a1d23',
	layoutForeground: '#ffffff',
	muted: '#f1f5f9',
	mutedForeground: '#64748b',
	accent: '#f1f5f9',
	accentForeground: '#212529',
	border: '#e2e8f0',
	input: '#e2e8f0',
	ring: '#ffbf00',
	// Forced single ink for chip/tag/pill labels riding on the brand color,
	// independent of the readable-contrast derivative used for body buttons —
	// overridable per tenant if their brand is light enough that white fails.
	tagBrandForeground: '#ffffff'
};

export const brandingSchema = z.object({
	primary: z
		.string({ message: 'Primary color is required' })
		.min(1, 'Primary color is required')
		.default(DEFAULT_COLORS.primary),
	primaryForeground: z
		.string({ message: 'Primary color text is required' })
		.min(1, 'Primary color text is required')
		.default(DEFAULT_COLORS.primaryForeground),
	secondary: z
		.string({ message: 'Secondary color is required' })
		.min(1, 'Secondary color is required')
		.default(DEFAULT_COLORS.secondary),
	secondaryForeground: z
		.string({ message: 'Secondary color text is required' })
		.min(1, 'Secondary color text is required')
		.default(DEFAULT_COLORS.secondaryForeground),
	info: z.string({ message: 'Info color is required' }).min(1, 'Info color is required').default(DEFAULT_COLORS.info),
	infoForeground: z
		.string({ message: 'Info color text is required' })
		.min(1, 'Info color text is required')
		.default(DEFAULT_COLORS.infoForeground),
	destructive: z
		.string({ message: 'Error color is required' })
		.min(1, 'Error color is required')
		.default(DEFAULT_COLORS.destructive),
	destructiveForeground: z
		.string({ message: 'Error color text is required' })
		.min(1, 'Error color text is required')
		.default(DEFAULT_COLORS.destructiveForeground),
	background: z
		.string({ message: 'Background color is required' })
		.min(1, 'Background color is required')
		.default(DEFAULT_COLORS.background),
	foreground: z
		.string({ message: 'Text color is required' })
		.min(1, 'Foreground color is required')
		.default(DEFAULT_COLORS.foreground),
	layout: z
		.string({ message: 'Layout color is required' })
		.min(1, 'Layout color is required')
		.default(DEFAULT_COLORS.layout),
	layoutForeground: z
		.string({ message: 'Layout color text is required' })
		.min(1, 'Layout color text is required')
		.default(DEFAULT_COLORS.layoutForeground)
});

export const brandingFormSchema = z.object({
	currentColorMode: z.enum(['light', 'dark']).default('light'),
	light: brandingSchema,
	dark: brandingSchema,
	logoAssignmentId: z.string().optional().nullable()
});

export { DEFAULT_COLORS };
